/**
 * Life4Earth V2 — Role Rhythm API adapter (DEV-1145 Phase 1i).
 *
 * Endpoints:
 *   GET /api/rolerhythms/instances/mine — open instances for the caller
 *     (pending / due / overdue / snoozed), ordered by due_at.
 *
 * Consumed by:
 *   - useApiRoleRhythmInstances → Engagements To-do tab
 *
 * The endpoint returns one row per open RoleRhythmTaskInstance:
 *   - recurring task instances surface with PeriodAnchor + DueAt populated
 *   - standing-duty instances surface with both fields null
 */

import { apiFetch } from "./client";
import { invalidateByPrefix } from "./queryCache";

// ════════════════════════════════════════════════════════════════
// Response types — mirror server-side RoleRhythmInstanceResponse
// (see DTOs/RoleRhythmDtos.cs).
// ════════════════════════════════════════════════════════════════

export type RoleRhythmInstanceStatus =
  | "pending"
  | "due"
  | "overdue"
  | "snoozed";

export type RoleRhythmTaskKind = "recurring_task" | "standing_duty";

export interface RoleRhythmInstanceResponse {
  instanceGuid: string;
  taskGuid: string;
  taskName: string;
  taskKind: RoleRhythmTaskKind | string;
  /** ISO date yyyy-MM-dd for recurring tasks; null for standing duties. */
  periodAnchor: string | null;
  dueAt: string | null;
  status: RoleRhythmInstanceStatus | string;
  snoozeCount: number;
  snoozedUntil: string | null;
  /** DEV-1201. Lead time (days before due) from the rhythm task definition. */
  leadTimeDays?: number;
  /** DEV-1201. Grace window (days after due before overdue). */
  dueWindowDays?: number;
  /** DEV-1209. Cadence type (null for standing duties) — tray "how often". */
  cadenceType?: string | null;
  /** DEV-1209. Raw cadence anchor JSON; formatted client-side for display. */
  cadenceAnchor?: string | null;
  /** DEV-1209. Task description — shown in the tray. */
  description?: string | null;
  /** DEV-1209. Community/org GUID of the instance's slot — for "Open Community". */
  communityEntityGuid?: string | null;
  /** DEV-1368. The slot this instance is anchored to — the STABLE key for
   *  matching an instance to its role (rename-proof, unlike the role name in
   *  sourceLabel). Null for non-slot rhythms. */
  roleSlotGuid?: string | null;
  /**
   * DEV-1164. Human-readable role-source label (e.g.,
   * "Hampton Rotary Club — Secretary"). Null when the slot's
   * source_definition_guid is unset or no community slot links
   * the rhythm to the caller. Surfaced as a small tag on each
   * To-do row so the user can see which role each item came from.
   */
  sourceLabel: string | null;

  /**
   * DEV-1251 P1. Checklist steps authored on the parent task — same
   * shape as RhythmTaskDetail.steps. Empty array when the task has
   * no steps; never undefined. Surfaced so the Action Center tray
   * can render the same step content as the Engagements
   * RhythmTaskTray without a second round-trip.
   */
  steps: RhythmStepDetail[];

  /**
   * DEV-1251 P1. Resolved contact-user identity from the parent
   * task's contact_user_guid. Null when no user-contact OR the
   * referenced user no longer exists (FK ON DELETE SET NULL).
   */
  contactUser: TaskContactUser | null;
}

// ════════════════════════════════════════════════════════════════
// DEV-1164 / DEV-1157 Phase 2g — rhythm details (composed view)
// ════════════════════════════════════════════════════════════════

/** One step under a rhythm task. Populated as of DEV-1234 iter 5. */
export interface RhythmStepDetail {
  stepGuid: string;
  label: string;
  description: string | null;
  /** DEV-1234 iter 5 follow-up. Optional hyperlink. Null = no link. */
  url: string | null;
  /** DEV-1240. Optional friendly label for the URL — user-facing surfaces
   *  render this as the clickable text and surface the full URL via
   *  hover tooltip. Null = fall back to rendering the URL itself. */
  urlLabel: string | null;
  isRequired: boolean;
  sortOrder: number;
}

/** DEV-1240 P1. Resolved contact-user identity surfaced inside
 *  RhythmTaskDetail.contactUser. AvatarUrl is null today (security_users
 *  has no avatar column) — render initials as fallback. */
export interface TaskContactUser {
  userGuid: string;
  name: string;
  avatarUrl: string | null;
}

/** One task under a rhythm. Returned inside RhythmDetailsResponse.tasks. */
export interface RhythmTaskDetail {
  taskGuid: string;
  /** "recurring_task" | "standing_duty" */
  taskKind: RoleRhythmTaskKind | string;
  name: string;
  description: string | null;
  /** One of the 10 cadence types; null for standing duties. */
  cadenceType: string | null;
  /** Raw JSON anchor; shape varies by cadenceType. Frontend parses for display. */
  cadenceAnchor: string | null;
  leadTimeDays: number;
  dueWindowDays: number;
  sortOrder: number;
  /** DEV-1240 P1. Raw contact GUIDs from role_rhythm_tasks. */
  contactUserGuid: string | null;
  contactRoleDefinitionGuid: string | null;
  /** Resolved contact identity for display. Null when no user-contact OR
   *  the referenced user no longer exists (FK ON DELETE SET NULL). */
  contactUser: TaskContactUser | null;
  steps: RhythmStepDetail[];
}

/**
 * Composed read-only view of a rhythm definition. Returned by
 * GET /api/rolerhythms/{rhythmGuid}/details. Consumed by the
 * Engagements role-detail right pane (RhythmDisplayPanel).
 */
export interface RhythmDetailsResponse {
  rhythmGuid: string;
  name: string;
  description: string | null;
  parentRhythmGuid: string | null;
  parentRhythmName: string | null;
  owningCommunityGuid: string | null;
  isSystemLocked: boolean;
  appliesToOrgRootGuid: string | null;
  tasks: RhythmTaskDetail[];
}

// ════════════════════════════════════════════════════════════════
// API
// ════════════════════════════════════════════════════════════════

/** Fetch the caller's open Role Rhythm instances. */
export async function fetchMyRoleRhythmInstances(): Promise<RoleRhythmInstanceResponse[]> {
  return apiFetch<RoleRhythmInstanceResponse[]>("/api/rolerhythms/instances/mine");
}

/**
 * DEV-1224 (2026-06-03). One materialized rhythm task instance for a
 * specific (slot, year) tuple. The full lifecycle status set applies
 * here (completed + skipped included) since this endpoint returns
 * history, not just open work.
 *
 * Audit metadata (CompletedAt / CompletedByDisplayName / SkipReason)
 * is populated alongside the basics — DEV-1225's historical tray
 * consumes this same shape, so the data is ready when that ticket
 * ships.
 */
export interface SlotRhythmInstanceResponse {
  instanceGuid: string;
  taskGuid: string;
  taskName: string;
  /** One of the 10 cadence types; standing duties are filtered out. */
  cadenceType: string | null;
  /** ISO yyyy-MM-dd. Required on this endpoint. */
  periodAnchor: string;
  dueAt: string | null;
  status:
    | "pending"
    | "due"
    | "overdue"
    | "snoozed"
    | "completed"
    | "skipped"
    | string;
  completedAt: string | null;
  completedByUserGuid: string | null;
  completedByDisplayName: string | null;
  /** Free-text. Common values: 'no_lead_orphan', 'manual_reconcile'. */
  skipReason: string | null;
  updatedAt: string;
}

/**
 * DEV-1224. Fetch all materialized rhythm task instances on a community
 * role slot for a given calendar year (defaults to current year when
 * the year arg is omitted). Powers the year-at-a-glance ribbon's
 * per-dot status glyph and the future historical-tray view (DEV-1225).
 */
export async function fetchSlotRhythmInstances(
  slotGuid: string,
  year?: number,
): Promise<SlotRhythmInstanceResponse[]> {
  const qs = typeof year === "number" ? `?year=${year}` : "";
  return apiFetch<SlotRhythmInstanceResponse[]>(
    `/api/rolerhythms/instances/by-slot/${slotGuid}${qs}`,
  );
}

// ════════════════════════════════════════════════════════════════
// DEV-1157 Phase 2b — Platform Rhythm Catalog (sysadmin authoring)
// ════════════════════════════════════════════════════════════════

/**
 * One row in the sysadmin Platform Rhythm Catalog list. Mirrors the
 * server's RoleRhythmAuthoringResponse — basic metadata only. Tasks /
 * steps are fetched via the existing details endpoint when the user
 * drills into a specific rhythm.
 */
export interface RoleRhythmAuthoringResponse {
  rhythmGuid: string;
  name: string;
  description: string | null;
  parentRhythmGuid: string | null;
  owningCommunityGuid: string | null;
  isSystemLocked: boolean;
  appliesToOrgRootGuid: string | null;
  isActive: boolean;
  createdByUserGuid: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * DEV-1157 Phase 2b. List all role rhythms for the Platform Rhythm
 * Catalog admin page. Sysadmin-only — returns all rhythms regardless
 * of ownership, active state, or system-lock.
 */
export async function fetchAllRhythmsAdmin(): Promise<RoleRhythmAuthoringResponse[]> {
  return apiFetch<RoleRhythmAuthoringResponse[]>("/api/rolerhythms/admin/list");
}

// ════════════════════════════════════════════════════════════════
// DEV-1234 — Admin catalog hierarchical view
// ════════════════════════════════════════════════════════════════

export interface AdminCatalogRhythmSummary {
  rhythmGuid: string;
  name: string;
  taskCount: number;
  isSystemLocked: boolean;
}

export interface AdminCatalogRole {
  definitionGuid: string;
  label: string;
  description: string | null;
  roleCategory: string;
  /**
   * DEV-1214 iter 3 (2026-06-04). The role's pillar — one of
   * geo_community / org_community / full_project / workspace / solution.
   * Surfaced so the Bundle authoring UI's "Add role" picker can
   * default-filter to the bundle's pillar(s).
   */
  pillarType: string;
  owningEntityGuid: string | null;
  isSystemLocked: boolean;
  /**
   * DEV-1214 iter 3 (2026-06-04). The Role Bundle this role belongs to,
   * or null if the role is universal core / per-org custom (not bundled).
   */
  roleBundleGuid: string | null;
  rhythm: AdminCatalogRhythmSummary | null;
}

export interface AdminCatalogPillar {
  pillarType: string;
  label: string;
  activeRoleCount: number;
  roles: AdminCatalogRole[];
}

export interface AdminCatalogResponse {
  pillars: AdminCatalogPillar[];
}

/**
 * DEV-1234. Hierarchical view of the platform role catalog for the
 * admin Rhythm page. Pillars → roles → rhythm summary in one round-trip.
 */
export async function fetchAdminCatalog(): Promise<AdminCatalogResponse> {
  return apiFetch<AdminCatalogResponse>("/api/rolerhythms/admin/catalog");
}

/**
 * DEV-1164. Fetch the composed view of a rhythm definition (metadata +
 * tasks + steps placeholder). Used by RhythmDisplayPanel on the
 * Engagements role-detail right pane.
 */
export async function fetchRhythmDetails(
  rhythmGuid: string,
): Promise<RhythmDetailsResponse> {
  return apiFetch<RhythmDetailsResponse>(`/api/rolerhythms/${rhythmGuid}/details`);
}

/**
 * DEV-1164. Fetch the rhythm details composed view starting from a
 * role_definitions GUID (the sourceDefinitionGuid carried on a role slot).
 * Returns null when the definition has no rhythm attached (204) or
 * doesn't exist (404). Frontend callers that have sourceDefinitionGuid
 * but not rhythmGuid use this to avoid an extra roundtrip.
 */
export async function fetchRhythmDetailsForDefinition(
  definitionGuid: string,
): Promise<RhythmDetailsResponse | null> {
  try {
    return await apiFetch<RhythmDetailsResponse>(
      `/api/rolerhythms/by-definition/${definitionGuid}`,
    );
  } catch (err) {
    // apiFetch surfaces non-2xx as throws; a 404 (no definition) or 204
    // (no rhythm attached) both render as "nothing to show". Caller
    // treats both as null.
    return null;
  }
}

/**
 * Mark an instance complete. Backend handles the recurring-vs-standing
 * fork: recurring tasks flip to status='completed' + materialize the
 * next instance; standing duties stay open + log a completion row.
 *
 * Returns 204 on success, 403 when caller is not the assignee, 404
 * when the instance doesn't exist, 409 if already terminal.
 */
export async function completeRoleRhythmInstance(
  instanceGuid: string,
): Promise<void> {
  await apiFetch<void>(
    `/api/rolerhythms/instances/${instanceGuid}/complete`,
    { method: "POST" },
  );
  // DEV-1224 — bust the by-slot rhythm-instances cache so the
  // RhythmDisplayPanel ribbon's per-dot status glyph reflects the new
  // 'completed' state on the next render. We bump by prefix because the
  // client doesn't carry the slot guid at this call site; the prefix
  // sweep is cheap (typically 0–1 keys in the cache).
  invalidateByPrefix("slotRhythmInstances:");
}

/**
 * DEV-1145 Phase 1g. Snooze an instance per Q7 caps:
 * 1 ≤ days ≤ 7, ≤ 2 snoozes per instance.
 *
 * Returns 204 on success. 409 if cap reached or already terminal.
 */
export async function snoozeRoleRhythmInstance(
  instanceGuid: string,
  days: number,
): Promise<void> {
  await apiFetch<void>(
    `/api/rolerhythms/instances/${instanceGuid}/snooze`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ days }),
    },
  );
  // DEV-1224 — snooze changes status to 'snoozed' on the open instance;
  // bust the by-slot cache so the ribbon stays current.
  invalidateByPrefix("slotRhythmInstances:");
}

/** DEV-1145 Phase 1h admin override actions. */
export type RoleRhythmOverrideAction =
  | "mark_completed"
  | "mark_skipped"
  | "reset_to_pending";

/**
 * DEV-1145 Phase 1h. Sysadmin override on an instance. Reason required.
 * Returns 403 to non-sysadmins; the UI should hide the affordance
 * unless the current user is a sysadmin.
 */
export async function overrideRoleRhythmInstance(
  instanceGuid: string,
  action: RoleRhythmOverrideAction,
  reason: string,
): Promise<void> {
  await apiFetch<void>(
    `/api/rolerhythms/instances/${instanceGuid}/override`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action, reason }),
    },
  );
  // DEV-1224 — sysadmin overrides flip status; bust the by-slot cache
  // so the ribbon reflects the new state.
  invalidateByPrefix("slotRhythmInstances:");
}

// ════════════════════════════════════════════════════════════════
// DEV-1234 iter 2 — Per-task editor (PUT /api/rolerhythms/tasks/{guid})
// ════════════════════════════════════════════════════════════════
//
// Patch shape mirrors the server-side UpdateRoleRhythmTaskRequest DTO.
// All fields optional; the controller treats null fields as no-ops and
// applies validation (cadence_type whitelist, lead/due >= 0, name not
// empty, etc.) per the cadence-required CHECK constraint from migration
// 193. task_kind is NOT editable post-create — to switch a recurring
// task to a standing duty or vice versa, retire and re-create.

export interface UpdateRoleRhythmTaskRequest {
  name?: string;
  description?: string | null;
  cadenceType?: string | null;
  /** Raw JSON string; server stores as JSONB. */
  cadenceAnchor?: string | null;
  leadTimeDays?: number;
  dueWindowDays?: number;
  sortOrder?: number;
  /** DEV-1240 P1. Three-state sentinel:
   *    null            → no-op
   *    "00000000-...-000000000000"  → clear (Guid.Empty signals "set to NULL")
   *    any other GUID  → set the contact to that user
   *  Mutually exclusive with contactRoleDefinitionGuid (server enforces). */
  contactUserGuid?: string | null;
  /** DEV-1240 P2+ schema-ready. Same three-state sentinel as contactUserGuid.
   *  Not populated by Phase 1 UI. */
  contactRoleDefinitionGuid?: string | null;
}

export interface RoleRhythmTaskAuthoringResponse {
  taskGuid: string;
  rhythmGuid: string;
  taskKind: string;
  name: string;
  description: string | null;
  cadenceType: string | null;
  cadenceAnchor: string | null;
  leadTimeDays: number;
  dueWindowDays: number;
  isActive: boolean;
  sortOrder: number;
  /** DEV-1240 P1. Raw contact GUIDs + resolved display. */
  contactUserGuid: string | null;
  contactRoleDefinitionGuid: string | null;
  contactUser: TaskContactUser | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * DEV-1234 iter 2. Edit a rhythm task. Sysadmin-only on the server side;
 * controller returns 403 to non-sysadmins. Pass only the fields you want
 * to change — null fields are no-ops, explicit null on description /
 * cadence_anchor clears the column.
 */
export async function updateRoleRhythmTask(
  taskGuid: string,
  patch: UpdateRoleRhythmTaskRequest,
): Promise<RoleRhythmTaskAuthoringResponse> {
  const result = await apiFetch<RoleRhythmTaskAuthoringResponse>(
    `/api/rolerhythms/tasks/${taskGuid}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    },
  );
  // The slot-by-year ribbon doesn't render task definitions directly,
  // but lead/due changes affect future materialized instances. Bust the
  // by-slot cache so the next ribbon refresh picks up the new windows.
  invalidateByPrefix("slotRhythmInstances:");
  return result;
}

// ════════════════════════════════════════════════════════════════
// DEV-1234 iter 3 — Create + retire task
// ════════════════════════════════════════════════════════════════

export interface CreateRoleRhythmTaskRequest {
  /** "recurring_task" or "standing_duty". Immutable post-create. */
  taskKind: string;
  name: string;
  description?: string | null;
  /** Required for recurring_task; MUST be null for standing_duty. */
  cadenceType?: string | null;
  cadenceAnchor?: string | null;
  leadTimeDays?: number;
  dueWindowDays?: number;
  sortOrder?: number;
  /** DEV-1240 P1. Optional contact at task creation. Mutually exclusive
   *  with contactRoleDefinitionGuid; server enforces. */
  contactUserGuid?: string | null;
  /** DEV-1240 P2+ schema-ready. Not populated by Phase 1 UI. */
  contactRoleDefinitionGuid?: string | null;
}

/**
 * DEV-1234 iter 3. Create a new task in a rhythm. Sysadmin-only on the
 * server side. The controller normalizes sort_order=0 to "next available"
 * within the rhythm, so callers can pass 0 to append.
 */
export async function createRoleRhythmTask(
  rhythmGuid: string,
  request: CreateRoleRhythmTaskRequest,
): Promise<RoleRhythmTaskAuthoringResponse> {
  const result = await apiFetch<RoleRhythmTaskAuthoringResponse>(
    `/api/rolerhythms/${rhythmGuid}/tasks`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(request),
    },
  );
  // New task → future instances will materialize for it. Bust the
  // by-slot cache so ribbons refresh on next view.
  invalidateByPrefix("slotRhythmInstances:");
  return result;
}

/**
 * DEV-1234 iter 3. Soft-retire a task. The controller sets is_active=false
 * on the row. Future instance materialization stops; existing open
 * instances continue to function until completed / skipped. The row stays
 * in the DB so existing references (audit log, completed instances) keep
 * working. Mirrors POLICY_CATALOG_PRESETS §7.2 pattern.
 */
export async function deleteRoleRhythmTask(taskGuid: string): Promise<void> {
  await apiFetch<void>(
    `/api/rolerhythms/tasks/${taskGuid}`,
    { method: "DELETE" },
  );
  invalidateByPrefix("slotRhythmInstances:");
}

// ════════════════════════════════════════════════════════════════
// DEV-1234 iter 4 — Create rhythm + attach to role definition
// DEV-1234 iter 6 — Update rhythm metadata (name, description)
// ════════════════════════════════════════════════════════════════

export interface CreateRoleRhythmRequest {
  name: string;
  description?: string | null;
  appliesToOrgRootGuid?: string | null;
}

export interface UpdateRoleRhythmRequest {
  /** Null = no-op (don't overwrite). Empty string is rejected by server. */
  name?: string;
  /** Null = no-op; empty string clears the description column. */
  description?: string | null;
  /** Phase 6+ scoping field. Phase 1 leaves this null. */
  appliesToOrgRootGuid?: string | null;
}

/**
 * DEV-1234 iter 4. Create a new RoleRhythm. Sysadmin only. Inheritance
 * (parent_rhythm_guid) is not yet supported by the backend (BD-3 lock).
 * Returns the created rhythm row.
 */
export async function createRoleRhythm(
  request: CreateRoleRhythmRequest,
): Promise<RoleRhythmAuthoringResponse> {
  return apiFetch<RoleRhythmAuthoringResponse>(
    `/api/rolerhythms`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(request),
    },
  );
}

/**
 * DEV-1234 iter 6. Update mutable fields on an existing rhythm. Sysadmin
 * only. Identity fields (rhythm_guid, owning_community_guid,
 * is_system_locked) are NOT editable post-create. Pass only the fields
 * you want to change — null fields are no-ops; pass empty string on
 * description to clear the column.
 */
export async function updateRoleRhythm(
  rhythmGuid: string,
  patch: UpdateRoleRhythmRequest,
): Promise<RoleRhythmAuthoringResponse> {
  return apiFetch<RoleRhythmAuthoringResponse>(
    `/api/rolerhythms/${rhythmGuid}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    },
  );
}

/**
 * DEV-1234 iter 4. Attach (or detach with rhythmGuid=null) a rhythm to
 * a role definition. Sysadmin only. Returns the updated role_definition
 * row. Backend gates against accidental overwrite — to swap an existing
 * rhythm for a different one, detach first then attach.
 */
export async function attachRhythmToRoleDefinition(
  definitionGuid: string,
  rhythmGuid: string | null,
): Promise<void> {
  await apiFetch<unknown>(
    `/api/admin/role-definitions/${definitionGuid}/attach-rhythm`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ rhythmGuid }),
    },
  );
}

// ════════════════════════════════════════════════════════════════
// DEV-1234 iter 9 — Role definition create + retire
// ════════════════════════════════════════════════════════════════

export interface CreateRoleDefinitionRequest {
  label: string;
  /** One of geo_community / org_community / full_project / workspace / solution. */
  pillarType: string;
  /** Free-text category — used for sub-grouping in the role list when a
   *  pillar has >10 roles. Examples: leadership, coordination, technical. */
  roleCategory: string;
  description?: string | null;
  /** Hint like "2-3 hours per month". Optional. */
  timeCommitmentHint?: string | null;
  skillsSuggested?: string[];
  concernCategories?: string[];
  /** JSONB Impact Signature weights; defaults to even (.2 each). */
  dimensionWeights?: string;
  /** Render order within a role_category group. 0 = default. */
  sortOrder?: number;
}

export interface RoleDefinitionResponse {
  definitionGuid: string;
  roleCategory: string;
  label: string;
  description: string | null;
  pillarType: string;
  isActive: boolean;
  isSystemLocked: boolean;
  owningEntityGuid: string | null;
  roleRhythmGuid: string | null;
  permissionSetGuid: string | null;
  sortOrder: number;
  timeCommitmentHint: string | null;
  skillsSuggested: string[];
  concernCategories: string[];
}

/**
 * DEV-1234 iter 9. Create a new role definition in the platform catalog.
 * Sysadmin only. The backend forces is_system_locked=true and
 * owning_entity_guid=null (sysadmin-managed canonical catalog rows).
 * Returns the created role definition.
 */
export async function createRoleDefinition(
  request: CreateRoleDefinitionRequest,
): Promise<RoleDefinitionResponse> {
  const body: Record<string, unknown> = {
    label: request.label,
    pillarType: request.pillarType,
    roleCategory: request.roleCategory,
    description: request.description ?? null,
    timeCommitmentHint: request.timeCommitmentHint ?? null,
    skillsSuggested: request.skillsSuggested ?? [],
    concernCategories: request.concernCategories ?? [],
    isActive: true,
    sortOrder: request.sortOrder ?? 0,
  };
  if (request.dimensionWeights) body.dimensionWeights = request.dimensionWeights;
  return apiFetch<RoleDefinitionResponse>(
    `/api/admin/role-definitions`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
}

/**
 * DEV-1234 iter 9. Soft-retire a role definition (is_active=false).
 * Sysadmin only. Existing community slots linked to this definition
 * keep working; new slot pickers hide retired roles. Attached rhythm
 * and any community overrides are NOT modified.
 */
export async function retireRoleDefinition(definitionGuid: string): Promise<void> {
  await apiFetch<void>(
    `/api/admin/role-definitions/${definitionGuid}`,
    { method: "DELETE" },
  );
}

// ════════════════════════════════════════════════════════════════
// DEV-1234 iter 10 — Clone role + clone task
// ════════════════════════════════════════════════════════════════

/**
 * DEV-1234 iter 10. Clone an existing role definition. Deep-copies the
 * role + (if attached) its rhythm + all tasks + all steps. New GUIDs
 * throughout. Sysadmin only. newLabel defaults server-side to
 * "{Original} (copy)" when omitted.
 */
export async function cloneRoleDefinition(
  definitionGuid: string,
  newLabel?: string,
): Promise<RoleDefinitionResponse> {
  return apiFetch<RoleDefinitionResponse>(
    `/api/admin/role-definitions/${definitionGuid}/clone`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ newLabel: newLabel ?? null }),
    },
  );
}

/**
 * DEV-1234 iter 10. Clone a single task (with its steps) into the
 * rhythm attached to a target role definition. Auto-creates the target
 * rhythm if missing. Sysadmin only. Cross-pillar clones are rejected
 * by the server (400 cross_pillar_clone_blocked).
 */
export async function cloneTaskToRole(
  sourceTaskGuid: string,
  targetDefinitionGuid: string,
): Promise<RoleRhythmTaskAuthoringResponse> {
  return apiFetch<RoleRhythmTaskAuthoringResponse>(
    `/api/rolerhythms/tasks/${sourceTaskGuid}/clone-to/${targetDefinitionGuid}`,
    { method: "POST" },
  );
}

// ════════════════════════════════════════════════════════════════
// DEV-1234 iter 5 — Step CRUD
// ════════════════════════════════════════════════════════════════

export interface CreateRoleRhythmTaskStepRequest {
  label: string;
  description?: string | null;
  /** DEV-1234 iter 5 follow-up. Optional hyperlink for the step. */
  url?: string | null;
  /** DEV-1240. Optional friendly label for the URL. Null/empty = fall
   *  back to displaying the URL itself on user-facing surfaces. */
  urlLabel?: string | null;
  isRequired?: boolean;
  /** 0 = append; backend assigns next sort_order in that task. */
  sortOrder?: number;
}

export interface UpdateRoleRhythmTaskStepRequest {
  label?: string;
  description?: string | null;
  /** Null = no-op; empty string clears. */
  url?: string | null;
  /** DEV-1240. Null = no-op; empty string clears the label. */
  urlLabel?: string | null;
  isRequired?: boolean;
  sortOrder?: number;
}

export interface RoleRhythmTaskStepResponse {
  stepGuid: string;
  taskGuid: string;
  label: string;
  description: string | null;
  /** DEV-1234 iter 5 follow-up. Optional hyperlink. */
  url: string | null;
  /** DEV-1240. Optional friendly label for the URL. */
  urlLabel: string | null;
  isRequired: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * DEV-1234 iter 5. Add a checklist step to a task. Sysadmin only.
 * sortOrder=0 (or omitted) appends — the controller assigns the next
 * available sort_order within the parent task.
 */
export async function createRoleRhythmTaskStep(
  taskGuid: string,
  request: CreateRoleRhythmTaskStepRequest,
): Promise<RoleRhythmTaskStepResponse> {
  return apiFetch<RoleRhythmTaskStepResponse>(
    `/api/rolerhythms/tasks/${taskGuid}/steps`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(request),
    },
  );
}

/**
 * DEV-1234 iter 5. Update a step's label / required flag / sort order.
 * Partial — null fields are no-ops; pass empty string on description to
 * clear it. Sysadmin only.
 */
export async function updateRoleRhythmTaskStep(
  stepGuid: string,
  patch: UpdateRoleRhythmTaskStepRequest,
): Promise<RoleRhythmTaskStepResponse> {
  return apiFetch<RoleRhythmTaskStepResponse>(
    `/api/rolerhythms/steps/${stepGuid}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    },
  );
}

/**
 * DEV-1234 iter 5. Hard-delete a step. Unlike tasks (which soft-retire
 * via is_active=false), steps don't carry an active flag — the table
 * was added late and the materializer joins on (task_guid, step_guid)
 * SQL-side, so removing the row is the correct shape. No instance-side
 * references exist that would break.
 */
export async function deleteRoleRhythmTaskStep(stepGuid: string): Promise<void> {
  await apiFetch<void>(
    `/api/rolerhythms/steps/${stepGuid}`,
    { method: "DELETE" },
  );
}
